import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { OrgConfig } from '../../services/api/touchlessService';
import { middlewareService } from '../../services/middleware/middlewareService';

export type AuthFlow = 'device' | 'visitor' | null;

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  shortToken: string | null;
  orgConfig: OrgConfig | null;
  isAuthenticated: boolean;
  authFlow: AuthFlow;
}

const initialState: AuthState = {
  accessToken: sessionStorage.getItem('accessToken'),
  refreshToken: sessionStorage.getItem('refreshToken'),
  shortToken: sessionStorage.getItem('prefillShortToken'),
  orgConfig: null,
  isAuthenticated: !!sessionStorage.getItem('accessToken'),
  authFlow: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTokens: (state, action: PayloadAction<{ accessToken: string; refreshToken?: string }>) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken || null;
      state.isAuthenticated = true;

      sessionStorage.setItem('accessToken', action.payload.accessToken);
      if (action.payload.refreshToken) {
        sessionStorage.setItem('refreshToken', action.payload.refreshToken);
      }
    },
    setShortToken: (state, action: PayloadAction<string>) => {
      state.shortToken = action.payload;
      sessionStorage.setItem('prefillShortToken', action.payload);
    },
    setOrgConfig: (state, action: PayloadAction<OrgConfig>) => {
      state.orgConfig = action.payload;
      sessionStorage.setItem('orgconfig', JSON.stringify(action.payload));

      // Set Middleware user context with org info
      middlewareService.setUser({
        id: action.payload.orgId?.toString() || 'unknown',
        orgId: action.payload.orgId,
        orgName: action.payload.orgName,
        siteId: action.payload.siteId,
        siteName: action.payload.siteName,
      });
    },
    setAuthFlow: (state, action: PayloadAction<AuthFlow>) => {
      state.authFlow = action.payload;
    },
    clearAuth: (state) => {
      state.accessToken = null;
      state.refreshToken = null;
      state.shortToken = null;
      state.orgConfig = null;
      state.isAuthenticated = false;
      state.authFlow = null;

      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('prefillShortToken');
      sessionStorage.removeItem('orgconfig');
      sessionStorage.removeItem('tempToken');

      // Clear Middleware user context on logout
      middlewareService.clearUser();
    },
  },
});

export const { setTokens, setShortToken, setOrgConfig, setAuthFlow, clearAuth } = authSlice.actions;
export default authSlice.reducer;
